'use client'

import ArrowUp from '@/assets/icons/arrow-up.svg'
import MenuIcon from '@/assets/icons/menu.svg'
import LogoLargeBlue from '@/assets/logo/logo-large-blue.svg'
import { ROUTES } from '@/core/constants/routes'
import { capitalizeFirstLetter } from '@/core/lib/utils/capitalize-first-letter'
import { CitySlugModel } from '@/features/cities/types/models/city-slug.model'
import { Link } from '@/i18n/navigation'
import { Drawer, DrawerBody, DrawerContent, DrawerHeader } from '@heroui/drawer'
import { useDisclosure } from '@heroui/react'
import { useTranslations } from 'next-intl'
import { use, useState } from 'react'
import { Button } from '../atoms/button'
import { NavLinks } from './nav-links'

type Props = {
  citySlugs: Promise<CitySlugModel[]>
}

export const HeaderDrawer: React.FC<Props> = ({ citySlugs }) => {
  const { isOpen, onOpenChange, onOpen } = useDisclosure()
  const [showCities, setShowCities] = useState(false)
  const cities = use(citySlugs)
  const t = useTranslations()

  return (
    <>
      <button
        onClick={onOpen}
        className='cursor-pointer'
        aria-label={t('ariaLabels.toggleMenu')}
        aria-expanded={isOpen}
        aria-controls='main-menu'
      >
        <MenuIcon className='fill-current' aria-hidden='true' />
      </button>

      <Drawer
        isOpen={isOpen}
        placement='left'
        onOpenChange={onOpenChange}
        radius='sm'
        size='xs'
      >
        <DrawerContent>
          <DrawerHeader className='shadow-navy h-20 shadow-xs'>
            <LogoLargeBlue />
          </DrawerHeader>
          <DrawerBody>
            <div className='flex flex-col gap-3 text-lg'>
              <NavLinks />

              <Button
                variant='text'
                onClick={() => setShowCities(prev => !prev)}
                className='flex w-fit cursor-pointer items-center gap-1 p-0 text-lg text-inherit'
              >
                {t('links.cities')}
                <ArrowUp
                  className={`mt-1 transform transition-transform duration-300 ${
                    showCities ? '' : 'rotate-180'
                  }`}
                />
              </Button>

              <ul
                className={`scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 relative flex flex-col gap-2 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 ${showCities ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} `}
              >
                <div className='pointer-events-none absolute top-0 right-0 left-0 h-8 bg-gradient-to-b from-gray-50/90 via-white/60 to-transparent' />
                {cities.map(city => (
                  <li key={city.id}>
                    <Link
                      href={ROUTES.city(city.slug)}
                      className='block h-full w-full px-3 py-2 text-base'
                      prefetch={false}
                    >
                      {capitalizeFirstLetter(city.slug?.split('-')?.[0] || '')}
                    </Link>
                  </li>
                ))}

                <div className='pointer-events-none absolute right-0 bottom-0 left-0 h-8 bg-gradient-to-t from-gray-50/90 via-white/60 to-transparent' />
              </ul>
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}
